import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaPhoneFlip, FaShip, } from "react-icons/fa6";
import { IoNavigate } from "react-icons/io5";
import { HiOutlineMail } from "react-icons/hi";
import { FaFacebookF, FaTwitter, FaLinkedinIn, FaInstagram, FaAngleDown, FaUserLock, FaSignOutAlt, FaCog, FaHandshake, FaChartBar } from "react-icons/fa";
import Logo from "../../assets/images/logo.png";
import { auth } from "../../services/api";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isCategoriesExpanded, setIsCategoriesExpanded] = useState(false);
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
  const [isPartnerMenuOpen, setIsPartnerMenuOpen] = useState(false);
  const [isDriverMenuOpen, setIsDriverMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isPartnerAuthenticated, setIsPartnerAuthenticated] = useState(false);
  const [isDriverAuthenticated, setIsDriverAuthenticated] = useState(false);
  const navigate = useNavigate();
  const adminDropdownRef = useRef(null);
  const partnerDropdownRef = useRef(null);
  const driverDropdownRef = useRef(null);
  useEffect(() => {
    // Check for admin, partner and driver tokens
    const adminToken = localStorage.getItem('Authentication:');
    const partnerToken = localStorage.getItem('partnerToken');
    const driverToken = localStorage.getItem('driverToken');
    setIsAuthenticated(!!adminToken);
    setIsPartnerAuthenticated(!!partnerToken);
    setIsDriverAuthenticated(!!driverToken);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (adminDropdownRef.current && !adminDropdownRef.current.contains(event.target)) {
        setIsAdminMenuOpen(false);
      }
      if (partnerDropdownRef.current && !partnerDropdownRef.current.contains(event.target)) {
        setIsPartnerMenuOpen(false);
      }
      if (driverDropdownRef.current && !driverDropdownRef.current.contains(event.target)) {
        setIsDriverMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePartnerClick = () => {
    navigate("/partner/login");
  };

  const handlePartnerLogout = () => {
    localStorage.removeItem("partnerToken");
    setIsPartnerAuthenticated(false);
    setIsPartnerMenuOpen(false);
    navigate("/");
  };

  const handlePartnerDashboard = () => {
    setIsPartnerMenuOpen(false);
    navigate("/partner/dashboard");
  };

  const handleDriverClick = () => {
    navigate("/driver/login");
  };

  const handleDriverLogout = () => {
    localStorage.removeItem("driverToken");
    localStorage.removeItem("driverData");
    setIsDriverAuthenticated(false);
    setIsDriverMenuOpen(false);
    navigate("/");
  };

  const handleDriverDashboard = () => {
    setIsDriverMenuOpen(false);
    navigate("/driver/dashboard");
  };
  const handleAdminClick = () => {
    navigate("/admin/login");
  };

  const handleLogout = async () => {
    try {
      localStorage.removeItem("adminToken");
      localStorage.removeItem("Authentication:");
      setIsAuthenticated(false);
      setIsAdminMenuOpen(false);
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
      localStorage.removeItem("adminToken");
      localStorage.removeItem("Authentication:");
      setIsAuthenticated(false);
      setIsAdminMenuOpen(false);
      navigate("/");
    }
  };

  const handleAdminPanel = () => {
    setIsAdminMenuOpen(false);
    navigate("/admin/dashboard");
  };

  // Updated options for yacht types
  const options = [
    { value: "Motor Yacht", label: "Motor Yachts", icon: FaShip },
    { value: "Sailing Yacht", label: "Sailing Yachts", icon: FaShip },
    { value: "Catamaran", label: "Catamarans", icon: FaShip },
    { value: "Cruiser", label: "Cruisers", icon: FaShip },
    { value: "Rib Boat", label: "Rib Boats", icon: FaShip },
    { value: "Caique", label: "Caiques", icon: FaShip }
  ];

  const handleCategoryClick = (e, category) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });

    navigate('/yachts', { 
      state: {
        selectedCategory: [category],
        timestamp: Date.now()
      },
      replace: true
    });
    setIsHovering(false);
    setIsMenuOpen(false); // Close mobile menu if open
    setIsCategoriesExpanded(false); // Close mobile categories
  };

  // Renamed and updated for yachts
  const handleYachtChartersClick = (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Navigate to the main yacht listing page without specific filters
    navigate('/yachts', { // Changed from '/search'
      state: {
        selectedCategory: [], // No specific category pre-selected
        // Add other relevant initial state if needed
      }
    });
    setIsMenuOpen(false); // Close mobile menu if open
  };

  const socialIcons = [
    { Icon: FaFacebookF, href: "/" },
    { Icon: FaTwitter, href: "/" },
    { Icon: FaLinkedinIn, href: "/" },
    { Icon: FaInstagram, href: "/" }
  ];

  const dropdownVariants = {
    hidden: { 
      opacity: 0, 
      y: 10,
      transition: {
        duration: 0.2
      }
    },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.3,
        staggerChildren: 0.05
      }
    },
    exit: {
      opacity: 0,
      y: 10,
      transition: {
        duration: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 }
  };

  const adminDropdownVariants = {
    hidden: { 
      opacity: 0,
      y: 10,
      scale: 0.95,
    },
    visible: { 
      opacity: 1,
      y: 0,
      scale: 1,
    }
  };

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setIsMenuOpen(false);

    if (!href) return;

    // Updated routes for yachts
    if (href === '/' || href === '/cruises') { // Changed '/transfers' to '/cruises' or similar
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate(href);
      return;
    }

    // If it's a hash link and not home page, navigate to home first
    if (href.startsWith('#') && window.location.pathname !== '/') {
      navigate('/', { state: { scrollTo: href } });
    } else if (href.startsWith('#')) {
      // If already on home page, just scroll
      const element = document.querySelector(href);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  };

  // useEffect to handle scrolling after navigation
  useEffect(() => {
    if (window.location.pathname === '/' && navigate?.location?.state?.scrollTo) {
      const element = document.querySelector(navigate.location.state.scrollTo);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
          // Clean up the state
          navigate('/', { replace: true });
        }, 100);
      }
    }
  }, [navigate?.location]);

  const navLinks = [
    { href: "/", text: "Home" },
    { href: "/yachts", text: "Yacht Charters", hasDropdown: true, onClick: handleYachtChartersClick }, 
    { href: "/cruises", text: "Cruises" },
    { href: "#services", text: "Services" }, 
    { href: "#about", text: "Contact" } 
  ];

  // mobileLinks to match navLinks
  const mobileLinks = [
    { href: "/", text: "Home" },
    { href: "/yachts", text: "Yacht Charters", onClick: handleYachtChartersClick },
    { href: "#cruises", text: "Cruises" },
    { href: "#services", text: "Services" },
    { href: "#contact", text: "Contact" }
  ];

  return (
        <div className="w-full bg-gradient-to-b from-blue-800 via-sky-700 to-sky-600 text-white border-b border-blue-400">

      <motion.div 
        className="w-full bg-white/10 border-b border-sky-200"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-[1440px] mx-auto flex flex-col md:flex-row justify-between items-center gap-4 text-white py-2 px-4">          
          <motion.div 
            className="flex flex-row flex-wrap justify-center items-center text-center gap-1 xs:gap-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            {/* Contact Info Items */}
            <motion.a 
              href="tel:+306941419012"
              className="flex flex-row items-center text-center gap-2 pr-3 border-r border-red-200"
              whileHover={{ scale: 1.05 }}
            >
              <FaPhoneFlip className="text-md text-yellow-300" />
              <span className="text-xs text-white">+30 69 41419012</span>
            </motion.a>
            <motion.a 
              href="mailto:nleont@gmail.com"
              className="flex flex-row items-center text-center gap-2 pl-2"
              whileHover={{ scale: 1.05 }}
            >
              <HiOutlineMail className="text-md text-yellow-300 md:border-yellow-200" />
              <span className="text-xs text-white">nleont@gmail.com</span>
            </motion.a>
            <motion.div 
              className="flex-row items-center text-center gap-2 pl-1 mr-2 md:flex hidden"
              whileHover={{ scale: 1.05 }}
            >
              <IoNavigate className="text-md text-yellow-300" />
              <span className="text-xs text-white">Epar. Od. Mykonos 846 00</span>
          </motion.div>
          </motion.div>

<div className="flex items-center gap-4">


            {/* Admin Button or Dropdown */}
            <div className="relative" ref={adminDropdownRef}>
              {isAuthenticated ? (
                <div>
                  <motion.button
                    onClick={() => setIsAdminMenuOpen(!isAdminMenuOpen)}
                    className="flex items-center gap-2 px-3 py-1 text-sm text-white hover:text-yellow-300 transition-colors duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaUserLock className="text-md" />
                    <span className="hidden sm:inline">Admin</span>
                    <FaAngleDown className={`transition-transform duration-300 ${isAdminMenuOpen ? 'rotate-180' : ''}`} />
                  </motion.button>
                  
                  <AnimatePresence>
                    {isAdminMenuOpen && (
                      <motion.div
                        variants={adminDropdownVariants}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50"
                      >
                        <motion.button
                          onClick={handleAdminPanel}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors duration-200"
                          whileHover={{ x: 4 }}
                        >
                          <FaCog className="mr-2" />
                          Admin Panel
                        </motion.button>
                        <motion.button
                          onClick={handleLogout}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors duration-200"
                          whileHover={{ x: 4 }}
                        >
                          <FaSignOutAlt className="mr-2" />
                          Logout
                        </motion.button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <motion.button
                  onClick={handleAdminClick}
                  className="flex items-center gap-2 px-3 py-1 text-sm text-white hover:text-yellow-300 transition-colors duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaUserLock className="text-md" />
                  <span className="hidden sm:inline">Admin</span>
                </motion.button>
              )}            </div>
            {/* Partner Login Button */}
            <div className="relative" ref={partnerDropdownRef}>
              {isPartnerAuthenticated ? (
                <div>
                  <motion.button
                    onClick={() => setIsPartnerMenuOpen(!isPartnerMenuOpen)}
                    className="flex items-center gap-2 px-3 py-1 text-sm text-white hover:text-yellow-300 transition-colors duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaHandshake className="text-md" />
                    <span className="hidden sm:inline">Partner</span>
                    <FaAngleDown className={`transition-transform duration-300 ${isPartnerMenuOpen ? 'rotate-180' : ''}`} />
                  </motion.button>

                  <AnimatePresence>
                    {isPartnerMenuOpen && (
                      <motion.div
                        variants={adminDropdownVariants}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50"
                      >
                        <motion.button
                          onClick={handlePartnerDashboard}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors duration-200"
                          whileHover={{ x: 4 }}
                        >
                          <FaChartBar className="mr-2" />
                          Dashboard
                        </motion.button>
                        <motion.button
                          onClick={handlePartnerLogout}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors duration-200"
                          whileHover={{ x: 4 }}
                        >
                          <FaSignOutAlt className="mr-2" /> 
                          Logout
                        </motion.button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                  ) : (
                  <motion.button
                    onClick={handlePartnerClick}
                    className="flex items-center gap-2 px-3 py-1 text-sm text-white hover:text-yellow-300 transition-colors duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaHandshake className="text-md" /> 
                    <span className="hidden sm:inline">Partner</span>
                  </motion.button>
                )}
            </div>
            
            {/* Driver Login Button */}
            {/* <div className="relative" ref={driverDropdownRef}>
              {isDriverAuthenticated ? (
                <div>
                  <motion.button
                    onClick={() => setIsDriverMenuOpen(!isDriverMenuOpen)}
                    className="flex items-center gap-2 px-3 py-1 text-sm text-blue-900 hover:text-yellow-600 transition-colors duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaShip className="text-md" />
                    <span className="hidden sm:inline">Driver</span>
                    <FaAngleDown className={`transition-transform duration-300 ${isDriverMenuOpen ? 'rotate-180' : ''}`} />
                  </motion.button>

                  <AnimatePresence>
                    {isDriverMenuOpen && (
                      <motion.div
                        variants={adminDropdownVariants}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50"
                      >
                        <motion.button
                          onClick={handleDriverDashboard}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors duration-200"
                          whileHover={{ x: 4 }}
                        >
                          <FaChartBar className="mr-2" />
                          Dashboard
                        </motion.button>
                        <motion.button
                          onClick={handleDriverLogout}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors duration-200"
                          whileHover={{ x: 4 }}
                        >
                          <FaSignOutAlt className="mr-2" /> 
                          Logout
                        </motion.button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                  ) : (
                  <motion.button
                    onClick={handleDriverClick}
                    className="flex items-center gap-2 px-3 py-1 text-sm text-blue-900 hover:text-yellow-600 transition-colors duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaShip className="text-md" /> 
                    <span className="hidden sm:inline">Driver</span>
                  </motion.button>
                )}
            </div> */}
          </div>
        </div>
      </motion.div>

      <motion.div 
        className="w-full bg-transparent shadow-md relative"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <header className="max-w-[1440px] mx-auto px-4">
          <nav className="flex flex-row w-full h-full items-center justify-between py-4">
            <motion.a
              href="/"
              onClick={(e) => handleNavClick(e, "/")}
              className="cursor-pointer"
              whileHover={{ scale: 1.05 }}
            >
              <motion.img 
                src={Logo} 
                alt="Seascape Experiences Logo" 
              className="h-20 sm:h-24 md:h-28 transition-all duration-300"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              />
            </motion.a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex flex-row space-x-4 uppercase items-center text-center my-auto h-full text-lg font-medium">
              <ul className="flex flex-row space-x-4 uppercase items-center text-center my-auto h-full font-semibold text-md">
                {navLinks.map((link, index) => (
                  <motion.li 
                    key={index}
                    className="group relative "
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <div className="flex items-center">
                      <a
                        href={link.href}
                        onClick={link.onClick || ((e) => handleNavClick(e, link.href))}
                        className="hover:text-yellow-500  ease-in-out transition-all duration-300"
                      >
                        {link.text}
                      </a>
                      {link.hasDropdown && (
                        <motion.div
                          className="relative group ml-3 flex gap-1 hover:text-yellow-500 ease-in-out transition-all duration-300 cursor-pointer hover:border-b-red-600"
                          onMouseEnter={() => setIsHovering(true)}
                          onMouseLeave={() => setIsHovering(false)}
                        >
                          Categories
                          <motion.div 
                            className="flex items-center cursor-pointer"
                            animate={{ rotate: isHovering ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            
                            <FaAngleDown />
                          </motion.div>
                          <AnimatePresence>
                            {isHovering && (
                              <motion.div
                                variants={dropdownVariants}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                                className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50 backdrop-blur-sm bg-white/90"
                              >
                                {options.map((option) => (
                                  <motion.div
                                    key={option.value}
                                    onClick={(e) => handleCategoryClick(e, option.value)}
                                    className="flex items-center px-4 py-3 hover:bg-blue-50 transition-all duration-200 group"
                                    variants={itemVariants}
                                    whileHover={{ x: 8 }}
                                  >
                                    <motion.div
                                      className="text-red-400 mr-3 group-hover:text-yellow-500"
                                      initial={{ scale: 1 }}
                                      whileHover={{ scale: 1.2 }}
                                      transition={{ type: "spring", stiffness: 400 }}
                                    >
                                      <option.icon />
                                    </motion.div>
                                    <span className="text-gray-700 group-hover:text-yellow-500 transition-colors duration-200">
                                      {option.label}
                                    </span>
                                  </motion.div>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      )}
                    </div>
                    <motion.span 
                      className="absolute -bottom-8 left-0 w-full h-0.5 bg-red-500"
                      initial={{ scaleX: 0 }}
                      whileHover={{ scaleX: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Mobile Menu Button */}
            <motion.div 
              className="lg:hidden flex items-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <motion.button 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="outline-none mobile-menu-button"
                whileTap={{ scale: 0.95 }}
              >
                <motion.svg
                  className="w-6 h-6 text-gray-500"
                  animate={{ rotate: isMenuOpen ? 90 : 0 }}
                  transition={{ duration: 0.3 }}
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M4 6h16M4 12h16m-7 6h7"></path>
                </motion.svg>
              </motion.button>
            </motion.div>
          </nav>
        </header>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div 
              className="mobile-menu absolute w-full bg-white shadow-lg"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <ul className="divide-y divide-gray-100">
                {mobileLinks.map((link, index) => (
                  <motion.li 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <a
                      href={link.href}
                      onClick={(e) => link.onClick ? link.onClick(e) : handleNavClick(e, link.href)}
                      className="block text-sm px-4 py-4 hover:bg-blue-500 hover:text-white transition duration: 0.3"
                    >
                      {link.text}
                    </a>
                  </motion.li>
                ))}
                {/* Mobile Categories Menu */}
                <motion.li
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: mobileLinks.length * 0.1 }}
                >
                  <button
                    onClick={() => setIsCategoriesExpanded(!isCategoriesExpanded)}
                    className="w-full px-4 py-4 flex justify-between items-center text-sm font-semibold text-gray-600 hover:bg-blue-50"
                  >
                    <span>Categories</span>
                    <motion.div
                      animate={{ rotate: isCategoriesExpanded ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <FaAngleDown />
                    </motion.div>
                  </button>
                  <AnimatePresence>
                    {isCategoriesExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden bg-gray-50"
                      >
                        {options.map((option, index) => (
                          <motion.a
                            key={option.value}
                            href={`#${option.value}`}
                            onClick={(e) => {
                              handleCategoryClick(e, option.value);
                              setIsMenuOpen(false);
                            }}
                            className="flex items-center px-8 py-3 hover:bg-blue-50 transition-all duration-200 text-sm"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                          >
                            <option.icon className="text-red-400 mr-3" />
                            <span className="text-gray-700">{option.label}</span>
                          </motion.a>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.li>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <style>
        {`
          @keyframes fadeIn {
              from {
                  opacity: 0;
                  transform: translateY(-10px);
              }
              to {
                  opacity: 1;
                  transform: translateY(18px);
              }
          }
          
          .mobile-menu {
            position: absolute;
            width: 100%;
            z-index: 40;
            top: 100%;
            left: 0;
            transform-origin: top;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          }

          .mobile-menu.visible {
            pointer-events: auto;
          }

          .mobile-menu.invisible {
            pointer-events: none;
          }
          
          @media (min-width: 1024px) {
            .mobile-menu {
              display: none;
            }
          }
        `}
      </style>
    </div>
  );
};

export default Header;
